import { useEffect } from 'react';
import { useSite } from './site';
import { useI18n } from './i18n';

function setMeta(attr: 'name' | 'property', key: string, content?: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

/**
 * Client-side head management for SPA navigation. The server injects the same
 * metadata into the initial HTML for crawlers (see server/src/services/seo.ts).
 */
export function useSeo(opts: { title?: string; description?: string; image?: string; noindex?: boolean; canonicalPath?: string }) {
  const site = useSite();
  const { L } = useI18n();
  useEffect(() => {
    const template = site.data?.seo.titleTemplate ?? '%s | Mana Yelamanchili';
    const title = opts.title ? template.replace('%s', opts.title) : L(site.data?.seo.defaultTitle) || 'Mana Yelamanchili';
    document.title = title;
    const desc = opts.description || L(site.data?.seo.defaultDescription);
    setMeta('name', 'description', desc?.slice(0, 300));
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc?.slice(0, 300));
    setMeta('property', 'og:image', opts.image || site.data?.seo.defaultOgImage || undefined);
    setMeta('name', 'robots', opts.noindex ? 'noindex, nofollow' : 'index, follow');
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = `${window.location.origin}${opts.canonicalPath ?? window.location.pathname}`;
  }, [opts.title, opts.description, opts.image, opts.noindex, opts.canonicalPath, site.data, L]);
}
